import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingAIButton from "./components/FloatingAIButton";
import activitiesImage from "./assests/activitiesImage.jpg"; // Add an activities image
import Modal from "./Modal";
import API_BASE_URL from "./config/api";

function Activities() {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract destinationId and itineraryDetails from location.state
  const destinationId = location.state?.destinationId;
  const itineraryDetails = location.state?.itineraryDetails;

  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [packageDetails, setPackageDetails] = useState(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/activities/destination/${destinationId}`,
          { method: "GET" }
        );
        const result = await response.json();
        setActivities(result);
      } catch (error) {
        console.error("Error fetching activities:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPackageDetails = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/packages/${itineraryDetails._id}`,
          { method: "GET" }
        );
        const result = await response.json();
        setPackageDetails(result);
      } catch (error) {
        console.error("Error fetching package details:", error);
      }
    };

    // Fetch data when destinationId changes
    if (destinationId) {
      fetchActivities();
      fetchPackageDetails();
    }
  }, [destinationId, itineraryDetails]);

  const handleSelectActivity = async (activityId) => {
    setSelectedActivity(activityId);
    if (packageDetails) {
      const updatedPackage = {
        ...packageDetails,
        itinerary: packageDetails.itinerary.map((day, index) => {
          if (index === location.state?.dayIndex) {
            return {
              ...day,
              destinations: day.destinations.map((dest) => ({
                ...dest,
                activities: [
                  {
                    activityId: activityId,
                  },
                ],
              })),
            };
          }
          return day;
        }),
      };

      try {
        setUpdating(true);

        const response = await fetch(
          `${API_BASE_URL}/packages/${packageDetails._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedPackage),
          }
        );

        if (response.ok) {
          const result = await response.json();
          setPackageDetails(result);
          alert("Package updated successfully!");
          // Navigate to trip details
          navigate(`/tripdetails?id=${packageDetails._id}`);
        } else {
          console.error("Error updating package:", await response.text());
          alert("Failed to update the package.");
        }
      } catch (error) {
        console.error("Error updating package:", error);
        alert("An error occurred while updating the package.");
      } finally {
        setUpdating(false);
      }
    }
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return <div style={styles.container}>Loading activities...</div>;
  }

  return (
    <>
      <Navbar />
      <div style={styles.destinationPage}>
        <header style={styles.destinationHeader}>
          <div style={styles.leftContent}>
            <h1 style={styles.h1}>Activities</h1>
          </div>
          <div>
            <img
              src={activitiesImage}
              alt="activities image"
              style={{
                width: 1350,
                height: 300,
                objectFit: "cover",
                objectPosition: "center",
                position: "relative",
              }}
            />
          </div>
        </header>
        <div
          style={{
            color: "#004E61",
            fontFamily: "Merriweather",
            fontSize: "45px",
            fontWeight: "700",
            lineHeight: "50px",
            textAlign: "center",
          }}
        >
          Available Activities
        </div>
      </div>
      <div style={styles.container}>
        <div style={styles.activityList}>
          {activities.length > 0 ? (
            activities.map((activity) => (
              <div key={activity._id} style={styles.activityCard}>
                <h2 style={styles.activityName}>{activity.name}</h2>
                <p style={styles.activityDetails}>
                  Description: {activity.description}
                </p>
                <p style={styles.activityDetails}>
                  Duration: {activity.duration} hours
                </p>
                {/* <p style={styles.activityDetails}>
                  Price: ₹{activity.rates}
                </p> */}
                <button
                  style={styles.selectButton}
                  onClick={() => handleSelectActivity(activity._id)}
                  disabled={updating}
                >
                  {updating && selectedActivity === activity._id
                    ? "Updating..."
                    : "Select"}
                </button>
              </div>
            ))
          ) : (
            <p style={styles.activityDetails}>No activities available</p>
          )}
        </div>
      </div>
      {/* Floating box with image */}
      <FloatingAIButton onClick={openModal} />
      {/* Modal */}
      {isModalOpen && <Modal closeModal={closeModal} />}
      <Footer />
    </>
  );
}

const styles = {
  destinationPage: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  destinationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    marginBottom: "20px",
  },
  leftContent: {
    position: "absolute",
    left: "100px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    color: "white",
  },
  h1: {
    fontFamily: '"Merriweather", serif',
    fontSize: "70px",
    fontWeight: 400,
    marginTop: "-30px",
    marginLeft: "-40px",
    color: "#004e61",
  },
  container: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  activityList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  activityCard: {
    border: "1px solid #D9D9D9",
    borderRadius: "15px",
    padding: "20px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  },
  activityName: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#004E61",
    marginBottom: "10px",
  },
  activityDetails: {
    fontSize: "14px",
    fontWeight: "300",
    color: "#616161",
    marginBottom: "10px",
  },
  selectButton: {
    backgroundColor: "#004E61",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "30px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    fontFamily: "Merriweather",
    marginTop: "10px",
  },
  floatingBox: {
    position: "fixed",
    bottom: "20px",
    right: "115px",
    width: "96px",
    height: "96px",
    display: "flex",
    borderRadius: "15px",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  floatingButton: {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    cursor: "pointer",
    borderRadius: "15px",
  },
  floatingImage: {
    width: "96px",
    height: "96px",
    objectFit: "cover",
    borderRadius: "15px",
  },
};

export default Activities;